import type React from 'react'
import type { PatientUser } from '~/shared/types'
import { Box, IconButton, Menu, MenuItem, Tooltip } from '@mui/material'
import MoreVert from '@mui/icons-material/MoreVert'
import { useState } from 'react'
import EnrollMemberModel from './EnrollMemberModel'
import ChurnMemberModel from './ChurnMemberModel'
import {
  DISCHARGE_IN_PROGRESS_TEXT,
  MISSING_DATA_INFO_TEXT,
  PRIMARY_CARE_STATUS,
} from '~/entities/program'
import CancelDischrgeModel from './CancelDischargeModel'
import { hasCareEnrollmentRequiredData, ONBOARDING_STATES } from '~/entities/person'
import EstablishMemberModal from './EstablishMemberModal'

interface PrimaryCareProgramMenuProps {
  enrollmentStatus: string | undefined
  patient: PatientUser
  churnedReason: string | undefined
  onboardingStatus?: string
}

const PrimaryCareProgramMenu = ({
  enrollmentStatus,
  patient,
  churnedReason,
  onboardingStatus,
}: PrimaryCareProgramMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<Element | null>(null)
  const [showEnrollModel, setshowEnrollModel] = useState<boolean>(false)
  const [showChurnModel, setshowChurnModel] = useState<boolean>(false)
  const [showCancelDischargeModel, setShowCancelDischargeModel] = useState<boolean>(false)
  const [showEstablishModal, setShowEstablishModal] = useState<boolean>(false)
  const [enrollmentLabel, setEnrollmentLabel] = useState<string>('Enroll')
  const [isChurned, setIsChurned] = useState<boolean>(true)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => setAnchorEl(null)

  const isDeactivated = onboardingStatus === ONBOARDING_STATES.deactivated
  const deactivatedTooltip = 'Member must be reactivated before enrollment actions'

  // Check if state information is present in insurance_info
  const hasRequiredData = hasCareEnrollmentRequiredData(patient)

  const missingDataTooltip = MISSING_DATA_INFO_TEXT
  const isEnrollmentDisabled = isDeactivated || !hasRequiredData
  const tooltipText = isDeactivated
    ? deactivatedTooltip
    : !hasRequiredData
      ? missingDataTooltip
      : ''

  const getMenuList = () => {
    const MenuList: React.JSX.Element[] = []
    if (!enrollmentStatus) {
      // Enroll member - disabled for deactivated members or missing state info
      MenuList.push(
        <Tooltip key="enroll-tooltip" title={tooltipText}>
          <span>
            <MenuItem
              key="enroll"
              disabled={isEnrollmentDisabled}
              onClick={() => {
                setshowEnrollModel(true)
                setAnchorEl(null)
                setEnrollmentLabel('Enroll')
              }}
            >
              Enroll member
            </MenuItem>
          </span>
        </Tooltip>,
      )
    } else if (churnedReason?.includes(DISCHARGE_IN_PROGRESS_TEXT)) {
      // Stop discharge process - disabled for deactivated members
      MenuList.push(
        <Tooltip key="cancel-discharge-tooltip" title={isDeactivated ? deactivatedTooltip : ''}>
          <span>
            <MenuItem
              key="cancel-discharge"
              disabled={isDeactivated}
              onClick={() => {
                setShowCancelDischargeModel(true)
                setAnchorEl(null)
              }}
            >
              Stop discharge process
            </MenuItem>
          </span>
        </Tooltip>,
      )
    } else if (enrollmentStatus === PRIMARY_CARE_STATUS.CHURNED) {
      // Re-enroll member - disabled for deactivated members or missing state info
      MenuList.push(
        <Tooltip key="reenroll-tooltip" title={tooltipText}>
          <span>
            <MenuItem
              key="reenroll"
              disabled={isEnrollmentDisabled}
              onClick={() => {
                setshowEnrollModel(true)
                setAnchorEl(null)
                setEnrollmentLabel('Re-enroll')
              }}
            >
              Re-enroll member
            </MenuItem>
          </span>
        </Tooltip>,
      )
    } else {
      MenuList.push(
        <MenuItem
          key="churn"
          onClick={() => {
            setshowChurnModel(true)
            setAnchorEl(null)
            setIsChurned(true)
          }}
        >
          Churn member
        </MenuItem>,
      )
      if (enrollmentStatus === PRIMARY_CARE_STATUS.ESTABLISHED) {
        MenuList.push(
          <MenuItem
            key="discharge"
            onClick={() => {
              setshowChurnModel(true)
              setAnchorEl(null)
              setIsChurned(false)
            }}
          >
            Discharge member
          </MenuItem>,
        )
      } else {
        MenuList.push(
          <MenuItem
            key="establish"
            onClick={() => {
              setShowEstablishModal(true)
              setAnchorEl(null)
            }}
          >
            Establish member
          </MenuItem>,
        )
      }
    }
    return MenuList
  }

  return (
    <Box sx={{ ml: 2 }}>
      <IconButton onClick={handleClick}>
        <MoreVert />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        slotProps={{
          list: {
            disablePadding: true,
          },
        }}
      >
        {getMenuList()}
      </Menu>
      <Box>
        {showEnrollModel && (
          <EnrollMemberModel
            patient={patient}
            handleClose={() => setshowEnrollModel(false)}
            open={showEnrollModel}
            enrollmentLabel={enrollmentLabel}
          />
        )}
        {showChurnModel && (
          <ChurnMemberModel
            patient={patient}
            handleClose={() => setshowChurnModel(false)}
            open={showChurnModel}
            isChurned={isChurned}
          />
        )}
        {showCancelDischargeModel && (
          <CancelDischrgeModel
            patient={patient}
            handleClose={() => setShowCancelDischargeModel(false)}
            open={showCancelDischargeModel}
          />
        )}
        {showEstablishModal && (
          <EstablishMemberModal
            patient={patient}
            handleClose={() => setShowEstablishModal(false)}
            open={showEstablishModal}
          />
        )}
      </Box>
    </Box>
  )
}

export default PrimaryCareProgramMenu
